import { ArrowRight, Bot, CircleCheck, CircleSlash, CornerUpLeft, Hourglass, Rocket, SquareTerminal, X } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { Link } from 'react-router';
import { Badge, Button, cn, DataTable, EmptyState, ErrorState, Panel, plural, RelativeTime, Sparkline, toast, toUiError, TONE_CLASS } from '@orgus/ui';
import type { Column, StatusTone } from '@orgus/ui';
import { DISCIPLINE_SENDBACK_LABEL } from '@orgus/protocol';
import type { DisciplineSendBack, DisciplineSendBackState, DisciplineState, Project, Session } from '@orgus/protocol';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { useDisciplineActions } from './actions.ts';
import { agentRows, sessionRows } from './agents.ts';
import type { AgentRow, SessionRow } from './agents.ts';
import { ListScore } from './FleetTable.tsx';
import { HealthBadge } from './HealthBadge.tsx';

function AgentCard({ a }: { a: AgentRow }) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-surface px-4 py-3 hairline">
      <div className="flex items-center gap-2">
        <Bot className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        <span className="truncate font-medium text-fg">{a.agent}</span>
      </div>
      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="flex items-baseline gap-1">
            <ListScore health={a.health} score={a.score ?? undefined} className="text-2xl" />
            <span className="text-sm text-fg-subtle">of 100</span>
          </span>
          <HealthBadge health={a.health} />
        </div>
        {a.trend.length > 1 ? (
          <Sparkline values={a.trend} min={0} max={100} width={96} height={32} tone="neutral" label={`${a.agent} score per hour`} />
        ) : (
          <span className="text-xs text-fg-subtle">No trend yet</span>
        )}
      </div>
      <span className="text-sm text-fg-subtle">
        {plural(a.sessions, 'session')}
        {a.launched ? `, ${a.launched} launched by Orgus` : ''}
        {a.errors + a.warns ? `, ${plural(a.errors, 'error')}, ${plural(a.warns, 'warning')}` : ''}
      </span>
    </div>
  );
}

const STATUS_LABEL: Record<Session['status'], string> = { working: 'Working', waiting: 'Needs you', idle: 'Idle', ended: 'Ended' };

/** The session's live state; waiting links to the session, where its question is answered. */
function LiveState({ r }: { r: SessionRow }) {
  if (!r.live) return <span>Gone</span>;
  if (r.live.status !== 'waiting') return <span>{STATUS_LABEL[r.live.status]}</span>;
  return (
    // Reads as a link at rest (underline and arrow), so it never looks like an action-less status.
    <Link
      to={`/sessions?session=${encodeURIComponent(r.live.id)}`}
      title="Open the session to answer it"
      aria-label={`Needs you: open ${r.title} to answer`}
      className="inline-flex items-center gap-1 rounded-control font-medium text-status-attention underline decoration-status-attention/40 underline-offset-2 transition-colors duration-(--duration-fast) hover:decoration-status-attention focus-visible:decoration-status-attention"
    >
      {STATUS_LABEL.waiting}
      <ArrowRight className="size-3" aria-hidden />
    </Link>
  );
}

function SessionCell({ r, phone }: { r: SessionRow; phone?: boolean }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-2">
        <SquareTerminal className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
        <span className={cn('font-medium text-fg', phone ? 'line-clamp-2 whitespace-normal' : 'truncate')}>{r.title}</span>
        {!phone && r.report.launched && (
          <Badge data-chip="" size="sm" tone="accent" icon={<Rocket />} className="shrink-0">
            Launched
          </Badge>
        )}
      </span>
      <span className="flex min-w-0 gap-2 text-caption text-fg-subtle">
        <span className="truncate">{r.projectName}</span>
        <span className="hidden truncate sm:inline">{r.report.agent}</span>
        <span className="shrink-0">
          <LiveState r={r} />
        </span>
      </span>
    </span>
  );
}

const sessionColumns: Column<SessionRow>[] = [
  {
    key: 'session',
    header: 'Session',
    grow: true,
    text: (r) => `${r.title} ${r.projectName} ${r.report.agent}`,
    sortValue: (r) => r.title.toLowerCase(),
    cell: (r) => <SessionCell r={r} />,
  },
  {
    key: 'score',
    header: 'Score',
    type: 'number',
    sortValue: (r) => (r.report.error ? -1 : r.report.score),
    cell: (r) => (
      <span className="flex items-center justify-end gap-2">
        {r.history.length > 1 && <Sparkline values={r.history.map((p) => p.score)} min={0} max={100} width={56} height={20} tone="neutral" label={`${r.title} score trend`} />}
        <ListScore health={r.health} score={r.report.score} className="w-7 text-right" />
      </span>
    ),
  },
  {
    key: 'found',
    header: 'New violations',
    type: 'number',
    hideBelow: 'sm',
    sortValue: (r) => r.report.errors * 1000 + r.report.warns,
    cell: (r) =>
      r.report.error ? (
        <span className="text-caption font-medium text-status-danger">Check failed</span>
      ) : r.report.errors + r.report.warns ? (
        <span className="text-sm text-fg-muted">
          {plural(r.report.errors, 'error')}, {plural(r.report.warns, 'warning')}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-caption text-fg-subtle">
          <CircleCheck className="size-4" aria-hidden /> Clean
        </span>
      ),
  },
  { key: 'files', header: 'Files changed', type: 'number', hideBelow: 'lg', sortValue: (r) => r.report.files, cell: (r) => <span className="num">{r.report.files}</span> },
  { key: 'checked', header: 'Checked', hideBelow: 'sm', sortValue: (r) => r.report.checkedAt, cell: (r) => <RelativeTime ts={r.report.checkedAt} className="text-caption text-fg-subtle" /> },
];

/** A phone row: the title on up to two lines over its repo and state, and the score inside the card padding. */
const phoneColumns: Column<SessionRow>[] = [
  { ...sessionColumns[0]!, cell: (r) => <SessionCell r={r} phone /> },
  { ...sessionColumns[1]!, cell: (r) => <ListScore health={r.health} score={r.report.score} /> },
];

const SB_ICON: Record<DisciplineSendBackState, ComponentType<{ className?: string }>> = { pending: Hourglass, delivered: CircleCheck, cancelled: CircleSlash, undeliverable: X };
const SB_TONE: Record<DisciplineSendBackState, StatusTone> = { pending: 'waiting', delivered: 'passed', cancelled: 'ended', undeliverable: 'failed' };

function SendBacks({ list, titles }: { list: DisciplineSendBack[]; titles: Map<string, string> }) {
  const actions = useDisciplineActions();
  const [busy, setBusy] = useState<string | null>(null);
  // A cancel the hub refused, said in the panel: errors never go in a toast.
  const [error, setError] = useState<unknown>(null);
  const cancel = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await actions.cancelSendBack(id);
      toast.success('Send-back cancelled', 'The session will not be told.');
    } catch (e) {
      setError(toUiError(e, { what: 'The send-back was not cancelled.', next: 'It may have been delivered already; reload to see.' }));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Panel title="Send-backs" sub={list.length ? plural(list.length, 'item') : undefined} icon={<CornerUpLeft />} flush>
      {error != null && <ErrorState variant="inline" error={error} className="m-3" />}
      {list.length ? (
        <ul className="divide-y divide-border-subtle">
          {list.slice(0, 20).map((sb) => {
            const Icon = SB_ICON[sb.state];
            return (
              // On a phone the state and time share the first line and the text takes the full width below, wrapping instead of cutting.
              <li key={sb.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                <span className={`inline-flex shrink-0 items-center gap-1.5 text-sm ${TONE_CLASS[SB_TONE[sb.state]].fg}`}>
                  <Icon className="size-3.5" aria-hidden />
                  {DISCIPLINE_SENDBACK_LABEL[sb.state]}
                </span>
                <span className="flex min-w-0 flex-1 flex-col max-sm:order-last max-sm:basis-full">
                  <span className="text-sm text-fg break-words sm:truncate">
                    {plural(sb.violations.length, 'violation')} to {titles.get(sb.sessionId) ?? sb.sessionId.slice(0, 8)}
                  </span>
                  <span className="text-xs text-fg-subtle break-words sm:truncate">{sb.reason ?? (sb.note || [...new Set(sb.violations.map((v) => v.rule))].join(', '))}</span>
                </span>
                <RelativeTime ts={sb.updatedAt} className="shrink-0 text-xs text-fg-subtle max-sm:ml-auto" />
                {sb.state === 'pending' && (
                  <Button size="sm" variant="ghost" onClick={() => void cancel(sb.id)} loading={busy === sb.id} className="max-sm:order-last">
                    Cancel send-back
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon={<CornerUpLeft />} title="Nothing sent back yet" description="Send back violations from the Violations tab to a session Orgus launched; it is told to fix them before it finishes." />
      )}
    </Panel>
  );
}

/** Discipline per agent and per live session, plus the send-backs on their way. */
export function SessionsView({ state, projects, sessions }: { state: DisciplineState | undefined; projects: Project[]; sessions: Record<string, Session> }) {
  const rows = useMemo(() => sessionRows(state, projects, sessions), [state, projects, sessions]);
  const agents = useMemo(() => agentRows(state), [state]);
  const titles = useMemo(() => new Map(rows.map((r) => [r.report.sessionId, r.title])), [rows]);
  const phone = useMediaQuery(NARROW_QUERY);
  if (!rows.length) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState
            icon={<SquareTerminal />}
            title="No session scores yet"
            description="Scores appear while sessions work in a watched repo."
            action={
              <Link to="/sessions" className="inline-flex">
                <Button variant="secondary" icon={<SquareTerminal />}>
                  Go to Sessions
                </Button>
              </Link>
            }
          />
        </div>
        <SendBacks list={state?.sendbacks ?? []} titles={titles} />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <section aria-label="Discipline per agent" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {agents.map((a) => (
          <AgentCard key={a.agent} a={a} />
        ))}
      </section>
      <DataTable label="Discipline per session" rows={rows} columns={phone ? phoneColumns : sessionColumns} rowKey={(r) => r.report.sessionId} searchable={{ placeholder: 'Filter sessions' }} pageSize={25} />
      <SendBacks list={state?.sendbacks ?? []} titles={titles} />
    </div>
  );
}
